"use client";
import type {
    APIAchievements,
    APIAppEnv,
    APIGamePreferences,
    APIUser,
    APIUserStats } from "@rosette/ts/src/business/api/api_schema.js";
import {
    toCompleteUser,
} from "@rosette/ts/src/business/api/api_schema.js";
import {
    DefaultGamePreferencesProvider,
    useGamePreferences,
} from "@/app/(api)/api/getGamePreferences/useGamePreferences.js";
import Header from "@/components/layout/Header.js";
import { DisplayName } from "@rosette/web_components/src/user/DisplayName.js";
import { HomePlayButtons } from "@/components/gamesetup/HomePlayButtons.js";
import type { ReactNode } from "react";
import React, { useEffect, useMemo, useState } from "react";
import { EnvProvider } from "@/components/env/EnvContext.js";
import { Permission } from "@rosette/ts/src/business/role/Permission.js";
import { Role } from "@rosette/ts/src/business/role/Role.js";
import { MatchmakingControls } from "@/components/gamesetup/MatchmakingControls.js";
import { Path } from "@rosette/ts/src/util/Path.js";
import type { LobbySettings } from "@rosette/ts/src/business/game/LobbySettings.js";
import { readPreferredSettings } from "@rosette/web_utils/src/local/readPreferredSettings.js";
import { useLobbyManagerState } from "@/utils/api/useLobbyManagerState.js";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { GameClientControls } from "@rosette/ts/src/business/GameClientControls.js";
import { BotType } from "@rosette/ts/src/business/game/BotType.js";
import { DeviceInfoProvider, useIsDesktop } from "@rosette/web_components/src/accessibility/DeviceInfoContext.js";
import { startGame } from "@/app/game/startGame.js";
import { useGameAPI, GameAPIProvider } from "@/components/api/GameAPIContext.js";
import { Panel } from "@rosette/web_components/src/ui/Panel.js";
import { HomeBackground } from "@rosette/web_components/src/layout/HomeBackground.js";
import { ArticleList } from "@/components/articles/ArticleList.js";
import { latestBlogPost } from "@/app/blog/blogPosts.js";
import { GameMode } from "@rosette/ts/src/business/game/GameMode.js";
import { LeaderboardWidget } from "@/components/leaderboard/LeaderboardWidget.js";
import { KnownLobbySettings } from "@rosette/ts/src/business/game/KnownLobbySettings.js";
import { LeaderboardType } from "@rosette/ts/src/business/api/leaderboard/LeaderboardType.js";
import { LeaderboardPeriod } from "@rosette/ts/src/business/api/leaderboard/LeaderboardPeriod.js";
import { LeaderboardGrouping } from "@rosette/ts/src/business/api/leaderboard/LeaderboardGrouping.js";
import { LeaderboardMetric } from "@rosette/ts/src/business/api/leaderboard/LeaderboardMetric.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import { DISCORD_INVITE_URL } from "@rosette/ts/src/business/constants.js";
import { showNotification } from "@rosette/web_utils/src/game/notifications.js";
import { RandomGame } from "@/components/game/RandomGame.js";
import { SmallSocialLinks } from "@/components/social/SmallSocialLinks.js";
import { rulesArticle } from "@/app/(articles)/(learn)/rules/rulesArticle.js";
import { AchievementType } from "@rosette/ts/src/business/achievement/AchievementType.js";
import { NudgeType } from "@rosette/ts/src/business/nudge/NudgeType.js";
import { NudgeCheckResult } from "@rosette/ts/src/business/nudge/NudgeCheckResult.js";
import { loadNudge } from "@rosette/web_utils/src/local/nudgeStorage.js";
import { checkSuggestPandaBotNudge } from "@rosette/ts/src/business/nudge/suggestPandaBotNudge.js";
import { logDebug } from "@rosette/ts/src/business/debug.js";
import { getRoleColour } from "@/components/icon/getRoleIcon.js";


const GREETING_LEFT_CLASSES = "col-1 row-2 xl:row-1 w-full max-w-sm xl:max-w-72";
const GREETING_RIGHT_CLASSES = "col-1 row-3 lg:col-2 lg:row-2 xl:col-3 xl:row-1 w-full max-w-sm xl:max-w-72";
const STAT_TEXT_CLASSES = "m-0 text-sm font-bold text-center";
const COUNT_LINK_CLASSES = cn(STAT_TEXT_CLASSES, "text-gold no-underline hover:underline");


interface GreetingStatProps {
    label: string;
    children: ReactNode;
}

function GreetingStat({ label, children }: GreetingStatProps) {
    return (
        <Panel className="flex flex-col items-center justify-center p-3 md:p-3">
            <p className={STAT_TEXT_CLASSES}>
                {label}
            </p>
            {children}
        </Panel>
    );
}


interface WelcomeH1Props {
    user: APIUser | null;
    newUserMode: boolean;
}


function WelcomeH1({ user, newUserMode }: WelcomeH1Props) {
    const { preferences } = useGamePreferences(user);
    const roles = Role.list(user);
    return (
        <>
            <h1 className="w-full mt-3 mb-4 text-4xl md:text-4xl font-bold leading-[1.3] tracking-[0.01em] text-center align-middle">
                {newUserMode && (
                    <>
                        Play the
                        {" "}
                        <span className={cn(
                            "text-emphasis whitespace-nowrap bg-clip-text bg-size-[100%] [-webkit-text-fill-color:transparent]",
                            "bg-[linear-gradient(180deg,hsl(36_90%_75%)_0%,hsl(36_90%_67%)_30%,hsl(32_90%_57%)_80%,hsl(32_90%_46%)_90%,hsl(32_90%_45%)_100%)]",
                            "[text-shadow:none] drop-shadow-md",
                        )}>
                            Royal Game of Ur!
                        </span>
                    </>
                )}

                {!newUserMode && (
                    <>
                        Welcome,
                        {" "}
                        <span>
                            <DisplayName
                                name={user ? user.displayName : (
                                    <>
                                        play-
                                        <span className="italic">
                                            ur
                                        </span>
                                    </>
                                )}
                                preferences={preferences}
                                roles={roles} />

                            <span className={!user ? "italic" : ""}>
                                !
                            </span>
                        </span>
                    </>
                )}
            </h1>

            {newUserMode && (
                <p className="my-3 mx-0 text-base text-center">
                    This game has been played for over 4500 years.
                    Play a game and find out why!
                </p>
            )}
        </>
    );
}


interface GreetingInternalProps {
    appEnv: APIAppEnv;
    stats: APIUserStats | null;
    achievements: APIAchievements | null;
    ratings: Record<string, number> | null;
}


function GreetingInternal({ appEnv, stats, achievements, ratings }: GreetingInternalProps) {
    const isDesktop = useIsDesktop();
    const user = appEnv.user;
    const completeUser = toCompleteUser(user);

    const router = useRouter();
    const [gameSettings, setGameSettings] = useState<KnownLobbySettings | null>(null);
    useEffect(() => {
        setGameSettings(
            readPreferredSettings(completeUser)
                .withMode(GameMode.COMPUTER),
        );
    }, [completeUser]);

    const newUserMode = (
        !user && (!gameSettings || gameSettings.equals(KnownLobbySettings.DEFAULT))
    );
    const roles = useMemo(() => Role.list(completeUser), [completeUser]);

    const totalAchievements = AchievementType.values().length;
    const earnedAchievements = achievements ? Object.keys(achievements).length : 0;

    const gameAPI = useGameAPI();
    const lobbyManagerState = useLobbyManagerState(gameAPI);

    const startGameMutation = useMutation({
        mutationFn: async (settings: LobbySettings) => startGame(router, completeUser, settings, gameAPI),
    });
    const clientControls: GameClientControls = useMemo(() => {
        return {
            startGame: (settings: LobbySettings) => startGameMutation.mutate(settings),
            startingGame: startGameMutation.isPending,
            startGameError: startGameMutation.error,
            clearError: () => startGameMutation.reset(),
            resignGame: () => {
                throw new Error("resignGame is unsupported");
            },
            abortGame: () => {
                throw new Error("abortGame is unsupported");
            },
            notify: showNotification,
        };
    }, [startGameMutation]);

    const pandaAvailable: boolean = (
        !!gameSettings && lobbyManagerState.isBotAvailable(BotType.PANDA, gameSettings.getRuleset())
    );
    const shouldCheckCanPlayPanda = useMemo(() => {
        if (!gameSettings || !user) {
            logDebug("Panda nudge: Not checking - missing gameSettings or user", {
                hasGameSettings: !!gameSettings,
                hasUser: !!user,
            });
            return false;
        }

        // Only check for non-memburs playing the Hard bot
        // in settings where the Panda is available
        if (!pandaAvailable) {
            logDebug("Panda nudge: Not checking - panda not available for ruleset", {
                ruleset: gameSettings.getRuleset().getName(),
            });
            return false;
        }
        if (gameSettings.getMode() !== GameMode.COMPUTER || gameSettings.getBotType() !== BotType.HARD) {
            logDebug("Panda nudge: Not checking - not playing Hard bot in Computer mode", {
                mode: gameSettings.getMode(),
                botType: gameSettings.getBotType(),
            });
            return false;
        }
        if (Permission.UNLIMITED_PANDA_GAMES.hasPermission(roles)) {
            logDebug("Panda nudge: Not checking - user has unlimited Panda games");
            return false;
        }

        const nudge = loadNudge(NudgeType.SUGGEST_PANDA_BOT);
        const nudgeResult = checkSuggestPandaBotNudge(nudge);
        logDebug("Panda nudge: Check result from nudge logic", {
            nudgeResult,
            consecutiveAccepts: nudge.getConsecutiveAccepts(),
            consecutiveDeclines: nudge.getConsecutiveDeclines(),
            skipsSinceLastShown: nudge.getSkipsSinceLastShown(),
            timeSinceLastConsideredMs: nudge.getTimeSinceLastConsideredMs(),
        });
        if (nudgeResult === NudgeCheckResult.SKIP) {
            nudge.markSkipped();
        }
        const willCheck = nudgeResult === NudgeCheckResult.SHOW;
        logDebug(`Panda nudge: Decision - ${willCheck ? "WILL CHECK" : "WILL NOT CHECK"} if user can play panda`);
        return willCheck;
    }, [gameSettings, user, roles, pandaAvailable]);

    const canPlayPandaQuery = useQuery({
        queryKey: ["checkCanPlayPanda", user?.publicID, gameSettings?.toJSON()],
        queryFn: async () => {
            if (!gameSettings)
                throw new Error("No game settings");

            return gameAPI.checkCanCreateLobby(
                gameSettings.getMode(),
                gameSettings.getGameSettings(),
                BotType.PANDA,
            );
        },
        enabled: shouldCheckCanPlayPanda,
        staleTime: 5 * 60 * 1000,
        refetchInterval: 5 * 60 * 1000,
        gcTime: Infinity,
        retry: false,
    });

    useEffect(() => {
        if (!shouldCheckCanPlayPanda)
            return;

        logDebug("Panda nudge: Query result", {
            isLoading: canPlayPandaQuery.isLoading,
            isError: canPlayPandaQuery.isError,
            error: canPlayPandaQuery.error,
            canCreate: canPlayPandaQuery.data?.can_create,
            data: canPlayPandaQuery.data,
        });
    }, [shouldCheckCanPlayPanda, canPlayPandaQuery.isLoading, canPlayPandaQuery.isError, canPlayPandaQuery.error, canPlayPandaQuery.data]);

    const shouldSuggestPandaBot: boolean = (
        shouldCheckCanPlayPanda && !!canPlayPandaQuery.data?.can_create
    );

    return (
        <HomeBackground className="relative flex flex-col items-center w-full box-border m-0">
            <Header />

            <div
                className={cn(
                    "grid grid-cols-1 grid-rows-[auto_auto_auto] justify-items-center gap-0",
                    "lg:grid-cols-2 lg:grid-rows-[auto_auto] lg:gap-6",
                    "xl:grid-cols-[minmax(18rem,400px)_minmax(25rem,1fr)_minmax(18rem,400px)] xl:grid-rows-[auto] xl:gap-[48px]",
                    "w-full max-w-7xl xl:max-w-[1600px] pt-0 px-3 lg:px-6 pb-6",
                )}>
                <div className="col-1 row-1 lg:col-[1/-1] xl:col-2 flex flex-col items-center w-full max-w-2xl">
                    <WelcomeH1
                        user={completeUser}
                        newUserMode={newUserMode} />

                    {!newUserMode || stats ? (
                        <div className="flex flex-row gap-3 w-full mb-6">
                            <GreetingStat label="Games">
                                <Link
                                    className={COUNT_LINK_CLASSES}
                                    href={(
                                        user && user.username
                                            ? `/u/${user.username}/games`
                                            : "/register"
                                    )}>
                                    {stats ? stats.games.toLocaleString() : "?"}
                                </Link>
                            </GreetingStat>
                            <GreetingStat label="Achievements">
                                <Link
                                    className={COUNT_LINK_CLASSES}
                                    href={(
                                        user && user.username
                                            ? `/u/${user.username}`
                                            : "/register"
                                    )}>
                                    {earnedAchievements}
                                    {" / "}
                                    {totalAchievements}
                                </Link>
                            </GreetingStat>
                            <GreetingStat label={!user ? "Account" : "Premium"}>
                                <p className={STAT_TEXT_CLASSES}>
                                    {user && roles.includes(Role.MEMBUR) && (
                                        <Link
                                            href="/premium"
                                            className={COUNT_LINK_CLASSES}
                                            style={{ color: getRoleColour(Role.MEMBUR) }}>
                                            Diamond Active
                                        </Link>
                                    )}
                                    {user && !roles.includes(Role.MEMBUR) && roles.includes(Role.GOLD) && (
                                        <Link
                                            href="/premium"
                                            className={COUNT_LINK_CLASSES}
                                            style={{ color: getRoleColour(Role.GOLD) }}>
                                            You have Gold
                                        </Link>
                                    )}
                                    {user && !roles.includes(Role.MEMBUR) && !roles.includes(Role.GOLD) && (
                                        <Link href="/premium">
                                            Upgrade to Gold
                                        </Link>
                                    )}

                                    {!user && (
                                        <Link href="/register">
                                            Sign up
                                        </Link>
                                    )}
                                </p>
                            </GreetingStat>
                        </div>
                    ) : (
                        <div className="pt-6" />
                    )}
                    <HomePlayButtons
                        clientControls={clientControls}
                        gameSettings={gameSettings}
                        user={user}
                        newUserMode={newUserMode}
                        shouldSuggestPandaBot={shouldSuggestPandaBot} />

                    {newUserMode && !isDesktop && (
                        <div className="w-full mt-6">
                            <RandomGame inArticle={true} />
                        </div>
                    )}

                    {!newUserMode && (
                        <h2 className="w-full">
                            Play Online
                        </h2>
                    )}

                    <div className={cn(
                        "w-full",
                        newUserMode ? "mt-6" : null,
                    )}>
                        <MatchmakingControls
                            clientControls={clientControls}
                            getWatchLink={lobbyID => new Path(`/game/${lobbyID}`)}
                            ratings={ratings}
                            small={newUserMode}
                            lobbyManagerState={lobbyManagerState} />
                    </div>
                </div>

                {!newUserMode && (
                    <div className={GREETING_LEFT_CLASSES}>
                        <h2>Latest News</h2>
                        <ArticleList
                            articles={[latestBlogPost]}
                            compact={true} />

                        <SmallSocialLinks
                            className="mt-4" />
                    </div>
                )}

                {newUserMode && isDesktop && (
                    <div className={GREETING_LEFT_CLASSES}>
                        <h2>Learn the Rules</h2>

                        <ArticleList
                            articles={[rulesArticle]}
                            compact={true} />
                    </div>
                )}

                {newUserMode && isDesktop && (
                    <div className={GREETING_RIGHT_CLASSES}>
                        <RandomGame
                            className="h-full"
                            orientation="vertical" />
                    </div>
                )}

                {!newUserMode && (
                    <div className={GREETING_RIGHT_CLASSES}>
                        <h2 className="mb-0">
                            Leaderboard
                        </h2>
                        <LeaderboardWidget
                            from={0}
                            limit={10}
                            generateDisplayLeaderboards={yearMonth => [
                                new LeaderboardType(
                                    LeaderboardPeriod.ALL_TIME,
                                    yearMonth,
                                    LeaderboardGrouping.FINKEL,
                                    LeaderboardMetric.RATING,
                                ),
                                new LeaderboardType(
                                    LeaderboardPeriod.ALL_TIME,
                                    yearMonth,
                                    LeaderboardGrouping.BLITZ,
                                    LeaderboardMetric.RATING,
                                ),
                                new LeaderboardType(
                                    LeaderboardPeriod.ALL_TIME,
                                    yearMonth,
                                    LeaderboardGrouping.MASTERS,
                                    LeaderboardMetric.RATING,
                                ),
                            ]} />

                        <Panel size="sm" className="mt-2 border border-transparent text-left">
                            <p>
                                <Link href="/leaderboards">
                                    View all leaderboards
                                </Link>
                            </p>
                        </Panel>

                        <p>
                            Our new rating system is in beta! Gain rating
                            by playing online ranked games.
                            {" "}
                            <a href={DISCORD_INVITE_URL}>
                                Join our Discord community.
                            </a>
                        </p>
                    </div>
                )}
            </div>
        </HomeBackground>
    );
}


interface GreetingProps {
    appEnv: APIAppEnv;
    stats: APIUserStats | null;
    achievements: APIAchievements | null;
    ratings: Record<string, number> | null;
    serverDefaultPreferences: APIGamePreferences | null;
}


export function Greeting({ appEnv, stats, achievements, ratings, serverDefaultPreferences }: GreetingProps) {
    const completeUser = toCompleteUser(appEnv.user);
    return (
        <DeviceInfoProvider>
            <EnvProvider appEnv={appEnv}>
                <GameAPIProvider>
                    <DefaultGamePreferencesProvider
                        user={completeUser}
                        defaultPreferences={serverDefaultPreferences}>

                        <GreetingInternal
                            appEnv={appEnv}
                            stats={stats}
                            achievements={achievements}
                            ratings={ratings} />
                    </DefaultGamePreferencesProvider>
                </GameAPIProvider>
            </EnvProvider>
        </DeviceInfoProvider>
    );
}
